import {
  type ExtractDocumentTypeFromTypedRxJsonSchema,
  type RxCollection,
  type RxJsonSchema,
  toTypedRxJsonSchema,
} from "rxdb";
import { useRxCollection, type useRxData } from "rxdb-hooks";
import { EXTERNAL_CODING_REFERENCE, SYNCABLE_META } from "./common";

export const RXDB_DISEASE_COLLECTION_NAME = "disease";

export const useRxCollectionDisease = (): RxCollection<Disease> | null =>
  useRxCollection<Disease>(RXDB_DISEASE_COLLECTION_NAME);

type UseRxData = Parameters<typeof useRxData<Disease>>;
export type DiseaseQueryConstructor = UseRxData["1"];

const CATALOGUE_CODING = {
  type: ["object", "null"],
  properties: {
    icd10: EXTERNAL_CODING_REFERENCE,
    icd11: EXTERNAL_CODING_REFERENCE,
  },
  required: ["icd10", "icd11"],
} as const;

const I18N = {
  type: "array",
  items: {
    type: "object",
    properties: {
      locale: {
        type: "string",
        enum: ["en", "fr", "es", "ar", "sw", "kam"],
      },
      categoryDisplayName: {
        type: "string",
      },
      displayName: {
        type: "string",
      },
      keywords: {
        type: ["array", "null"],
        items: { type: "string" },
      },
    },
    required: ["locale", "categoryDisplayName", "displayName", "keywords"],
  },
} as const;

const DISEASE_SCHEMA_LITERAL = {
  title: "disease schema",
  version: 3,
  description: "Represents a disease",
  type: "object",
  primaryKey: "id",
  properties: {
    id: {
      type: "string",
      maxLength: 100,
    },
    meta: SYNCABLE_META,
    category: {
      type: "string",
    },
    catalogueCoding: CATALOGUE_CODING,
    i18n: I18N,
  },
  // https://elephant.slab.com/posts/offline-schema-field-encryption-policy-uwb0pbii#hq2b6-disease
  required: ["id", "meta", "category", "catalogueCoding", "i18n"],
  /*
   * It is important to set 'as const' to preserve the literal type.
   *
   * biome-ignore lint/suspicious/noExplicitAny: We infer the DB type from this
   * object, the satisfies just provides us type inference for this object.
   */
} as const satisfies RxJsonSchema<any>;

const schemaTyped = toTypedRxJsonSchema(DISEASE_SCHEMA_LITERAL);

export type Disease = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>;

export const DISEASE_SCHEMA: RxJsonSchema<Disease> = DISEASE_SCHEMA_LITERAL;
